import React, { useState } from 'react';

/*
  Profile — user profile management page extracted from App.js.
  Props:
    user       — current user object
    setUser    — App.js user setter (to update display name after save)
    navigateTo — navigation helper
    goBack     — back navigation helper
*/
const Profile = ({ user, setUser, navigateTo, goBack }) => {
  const [profileData, setProfileData] = useState({
    name: '',
    phone: '',
    dateOfBirth: '',
    city: '',
    occupation: '',
    annualIncome: '',
  });
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileError, setProfileError] = useState('');
  const [profileSuccess, setProfileSuccess] = useState(false);

  const handleProfileSave = async (e) => {
    e.preventDefault();
    setProfileSaving(true);
    setProfileError('');
    setProfileSuccess(false);
    try {
      const res = await fetch('/api/users/profile', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(profileData),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to save profile');
      setUser(prev => ({ ...prev, name: data.user.name }));
      setProfileSuccess(true);
      setTimeout(() => setProfileSuccess(false), 3000);
    } catch (err) {
      setProfileError(err.message);
    } finally {
      setProfileSaving(false);
    }
  };

  const inputStyle = {
    width: '100%',
    padding: '12px 16px',
    borderRadius: '8px',
    border: '1px solid var(--border)',
    backgroundColor: 'var(--bg)',
    color: 'var(--text-primary)',
    fontSize: '15px',
    outline: 'none',
    transition: 'border-color 0.2s',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
  };

  const sectionLabel = {
    color: 'var(--text-tertiary)',
    fontSize: '11px',
    fontWeight: 600,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    marginBottom: '12px',
  };

  const btnPrimary = {
    backgroundColor: 'var(--accent)',
    color: '#ffffff',
    border: 'none',
    borderRadius: '9px',
    fontWeight: 700,
    cursor: 'pointer',
    transition: 'background-color 0.2s ease',
    fontFamily: 'inherit',
  };
  const btnPrimaryHover = (e) => {
    e.currentTarget.style.backgroundColor = 'var(--accent-hover)';
  };
  const btnPrimaryLeave = (e) => {
    e.currentTarget.style.backgroundColor = 'var(--accent)';
  };

  const btnGhost = {
    background: 'transparent',
    color: 'var(--text-primary)',
    border: '1px solid var(--border)',
    borderRadius: '9px',
    fontWeight: 700,
    cursor: 'pointer',
    transition: 'border-color 0.2s ease',
    fontFamily: 'inherit',
  };
  const btnGhostHover = (e) => {
    e.currentTarget.style.borderColor = 'var(--text-tertiary)';
  };
  const btnGhostLeave = (e) => {
    e.currentTarget.style.borderColor = 'var(--border)';
  };

  return (
    <section style={{ minHeight: '100vh', backgroundColor: 'var(--bg)', paddingTop: '80px' }}>
      <div style={{ maxWidth: '640px', margin: '0 auto', padding: '48px 24px' }}>

        {/* Back link */}
        <button
          onClick={() => goBack()}
          style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', fontSize: '13px', fontWeight: 500, cursor: 'pointer', padding: 0, marginBottom: '48px', fontFamily: 'inherit', transition: 'color 0.2s' }}
          onMouseEnter={e => e.currentTarget.style.color = 'var(--text-primary)'}
          onMouseLeave={e => e.currentTarget.style.color = 'var(--text-secondary)'}
        >← Back</button>

        {/* Heading */}
        <div style={{ marginBottom: '36px' }}>
          <p style={sectionLabel}>Account</p>
          <h1 style={{ fontSize: '32px', fontWeight: 600, color: 'var(--text-primary)', letterSpacing: '-0.02em', margin: 0 }}>Your Profile</h1>
        </div>

        {/* Form card */}
        <div style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: '16px', padding: '36px' }}>
          <form onSubmit={handleProfileSave}>

            {/* Row: Full Name */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '8px', letterSpacing: '0.03em' }}>Full Name</label>
              <input
                type="text"
                value={profileData.name || user?.name || ''}
                onChange={e => setProfileData(p => ({ ...p, name: e.target.value }))}
                style={inputStyle}
                onFocus={e => e.currentTarget.style.borderColor = 'var(--accent)'}
                onBlur={e => e.currentTarget.style.borderColor = 'var(--border)'}
                placeholder="Your full name"
              />
            </div>

            {/* Row: Email (read-only) */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '8px', letterSpacing: '0.03em' }}>Email</label>
              <input
                type="email"
                value={user?.email || ''}
                readOnly
                style={{ ...inputStyle, color: 'var(--text-tertiary)', cursor: 'default', backgroundColor: 'var(--bg-secondary)' }}
              />
            </div>

            {/* Row: Phone + Date of Birth */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '8px', letterSpacing: '0.03em' }}>Phone Number</label>
                <input
                  type="tel"
                  value={profileData.phone}
                  onChange={e => setProfileData(p => ({ ...p, phone: e.target.value }))}
                  style={inputStyle}
                  onFocus={e => e.currentTarget.style.borderColor = 'var(--accent)'}
                  onBlur={e => e.currentTarget.style.borderColor = 'var(--border)'}
                  placeholder="+91 00000 00000"
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '8px', letterSpacing: '0.03em' }}>Date of Birth</label>
                <input
                  type="date"
                  value={profileData.dateOfBirth}
                  onChange={e => setProfileData(p => ({ ...p, dateOfBirth: e.target.value }))}
                  style={{ ...inputStyle, colorScheme: 'light' }}
                  onFocus={e => e.currentTarget.style.borderColor = 'var(--accent)'}
                  onBlur={e => e.currentTarget.style.borderColor = 'var(--border)'}
                />
              </div>
            </div>

            {/* Row: City + Occupation */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '8px', letterSpacing: '0.03em' }}>City</label>
                <input
                  type="text"
                  value={profileData.city}
                  onChange={e => setProfileData(p => ({ ...p, city: e.target.value }))}
                  style={inputStyle}
                  onFocus={e => e.currentTarget.style.borderColor = 'var(--accent)'}
                  onBlur={e => e.currentTarget.style.borderColor = 'var(--border)'}
                  placeholder="Mumbai"
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '8px', letterSpacing: '0.03em' }}>Occupation</label>
                <input
                  type="text"
                  value={profileData.occupation}
                  onChange={e => setProfileData(p => ({ ...p, occupation: e.target.value }))}
                  style={inputStyle}
                  onFocus={e => e.currentTarget.style.borderColor = 'var(--accent)'}
                  onBlur={e => e.currentTarget.style.borderColor = 'var(--border)'}
                  placeholder="Software Engineer"
                />
              </div>
            </div>

            {/* Row: Annual Income + Account Type */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '32px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '8px', letterSpacing: '0.03em' }}>Annual Income (approx.)</label>
                <input
                  type="text"
                  value={profileData.annualIncome}
                  onChange={e => setProfileData(p => ({ ...p, annualIncome: e.target.value }))}
                  style={inputStyle}
                  onFocus={e => e.currentTarget.style.borderColor = 'var(--accent)'}
                  onBlur={e => e.currentTarget.style.borderColor = 'var(--border)'}
                  placeholder="e.g. 12,00,000"
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '8px', letterSpacing: '0.03em' }}>Account Type</label>
                <input
                  type="text"
                  value={user?.accountType === 'personal' ? 'Personal' : 'Business'}
                  readOnly
                  style={{ ...inputStyle, color: 'var(--text-tertiary)', cursor: 'default', backgroundColor: 'var(--bg-secondary)' }}
                />
              </div>
            </div>

            {/* Error / success feedback */}
            {profileError && (
              <div style={{ marginBottom: '20px', padding: '12px 16px', borderRadius: '8px', backgroundColor: 'rgba(244,63,94,0.08)', border: '1px solid rgba(244,63,94,0.2)', color: '#F43F5E', fontSize: '14px' }}>
                {profileError}
              </div>
            )}
            {profileSuccess && (
              <div style={{ marginBottom: '20px', padding: '12px 16px', borderRadius: '8px', backgroundColor: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.2)', color: '#10B981', fontSize: '14px' }}>
                Profile saved successfully.
              </div>
            )}

            {/* Actions */}
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                type="submit"
                disabled={profileSaving}
                style={{ ...btnPrimary, padding: '12px 28px', fontSize: '15px', opacity: profileSaving ? 0.7 : 1 }}
                onMouseEnter={profileSaving ? undefined : btnPrimaryHover}
                onMouseLeave={profileSaving ? undefined : btnPrimaryLeave}
              >{profileSaving ? 'Saving...' : 'Save Changes'}</button>
              <button
                type="button"
                onClick={() => navigateTo(user?.accountType === 'personal' ? 'personal-dashboard' : 'dashboard')}
                style={{ ...btnGhost, padding: '12px 24px', fontSize: '15px' }}
                onMouseEnter={btnGhostHover}
                onMouseLeave={btnGhostLeave}
              >Cancel</button>
            </div>

          </form>
        </div>

      </div>
    </section>
  );
};

export default Profile;
